import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import GlowSolutionHighlight from "@/components/GlowSolutionHighlight";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Star, Eye, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import facialImage from "@/assets/gesichtsbehandlung-facial-nuernberg.webp";
import facialVideo from "@/assets/gesichtsbehandlung-video-nuernberg.webm";
import glowMachine2 from "@/assets/glowsolution-hautpflege-behandlung.jpg";
import lashesImage from "@/assets/lash-brow-lift-behandlung-nuernberg.webp";
import SEO from "@/components/SEO";

const PLANITY_URL = "https://www.planity.com/de-DE/22-studios-90469-nurnberg";

const FacialsPage = () => {
  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Facial Nürnberg – Aquafacial & Skincare | 22 Studios"
        description="Individuelle Gesichtsbehandlung in Nürnberg mit GlowSolution Aquafacial, Mikroneedling & iS Clinical Produkten. Sichtbar strahlende Haut ab der ersten Sitzung."
        canonical="https://22studios.de/nuernberg/facials"
      />
      <AnimatedBackground />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-10 md:pt-40 md:pb-24 relative z-10">
        <div className="container px-4 md:px-6">

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-20 items-center">
            <div className="space-y-4 md:space-y-6">
              <div className="space-y-2 md:space-y-4">
                <span className="text-sm tracking-wide uppercase text-label font-medium">
                  Facial & Gesichtsbehandlung
                </span>
                <h1 className="text-2xl md:text-display-lg font-serif font-light text-foreground">
                  Gesichtsbehandlung in Nürnberg
                </h1>
              </div>
              <p className="text-sm md:text-lg text-muted-foreground font-light leading-relaxed">
                Professionelle Gesichtsbehandlung in Nürnberg für strahlende Haut. Erlebe moderne Facials mit GlowSolution Aquafacial, Mikroneedling und exklusiven iS Clinical Treatments – dein Facial in Nürnberg für sichtbare Ergebnisse.
              </p>
              <div className="flex flex-wrap gap-1.5 md:gap-2">
                {["GlowSolution", "Aquafacial", "Facial", "Mikroneedling"].map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-blush-dark px-6 md:px-8 py-5 md:py-6 text-sm md:text-base font-medium rounded-full hover-lift shadow-glow w-full sm:w-auto"
                onClick={() => window.open(PLANITY_URL, '_blank')}
              >
                Jetzt Termin buchen
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2.5 md:gap-4 items-start">
              <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-soft-lg hover-pop">
                <video
                  src={facialVideo}
                  poster={facialImage}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-soft-lg hover-pop mt-6 md:mt-8">
                <img
                  src={glowMachine2}
                  alt="GlowSolution Aquafacial Gerät Nürnberg – 22 Studios"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GlowSolution Highlight Section */}
      <section className="py-10 md:py-20 relative z-10">
        <div className="container px-4 md:px-6">
          <GlowSolutionHighlight variant="full" />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-10 md:py-28 bg-secondary/30 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-6 md:mb-16">
            <div className="flex items-center justify-center gap-4 mb-3 md:mb-6">
              <span className="w-8 md:w-12 h-px bg-primary" />
              <span className="text-xs md:text-sm tracking-wide uppercase text-label font-medium">
                Preise
              </span>
              <span className="w-8 md:w-12 h-px bg-primary" />
            </div>
            <h2 className="text-xl md:text-display font-serif font-light text-foreground">
              Facial Preise – Gesichtsbehandlung Nürnberg
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-5xl mx-auto">
            {/* IS Clinical Facials */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Star className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Signature Facials von iS Clinical</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="flex justify-between items-center py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">Quick Facial</span>
                  <span className="font-medium">49 €</span>
                </li>
                <li className="flex justify-between items-center py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">Fire & Ice Facial</span>
                  <span className="font-medium">119 €</span>
                </li>
                <li className="flex justify-between items-center py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">Foaming Enzym Facial</span>
                  <span className="font-medium">119 €</span>
                </li>
                <li className="flex justify-between items-center py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">Honey Enzym</span>
                  <span className="font-medium">99 €</span>
                </li>
                <li className="flex justify-between items-center py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">The Gentleman / Man's Treatment</span>
                  <span className="font-medium">119 €</span>
                </li>
              </ul>
            </div>

            {/* Glow Solution Treatments */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Glow Solution Treatments</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground text-sm">"AQUAFACIAL"</span>
                    <span className="font-medium">99 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Tiefreinigung & Glow</p>
                </li>
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground text-sm">"GET YOUR GLOW"</span>
                    <span className="font-medium">119 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Tiefreinigung, Deep Peeling, Feuchtigkeitsboost</p>
                </li>
                <li className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground text-sm">"ANTI AGING"</span>
                    <span className="font-medium">139 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Tiefreinigung, Peeling, Needling & Glow Push Up</p>
                </li>
              </ul>
            </div>

            {/* Extras */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group md:col-span-2">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Extras</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div className="flex justify-between items-center py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">LED Light Therapy</span>
                  <span className="font-medium">10 €</span>
                </div>
                <div className="flex justify-between items-center py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <span className="text-muted-foreground text-sm">Gesichtsmassage</span>
                  <span className="font-medium">20 €</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10 md:mt-16">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
              onClick={() => window.open(PLANITY_URL, '_blank')}
            >
              Jetzt Termin buchen
            </Button>
          </div>
        </div>
      </section>

      {/* Lash- & Brow Lift Section */}
      <section className="py-12 md:py-28 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-20 items-center mb-8 md:mb-16">
            <div className="space-y-3 md:space-y-6">
              <div className="space-y-2 md:space-y-4">
                <span className="text-sm tracking-wide uppercase text-label font-medium">
                  Lash- & Brow Lift
                </span>
                <h2 className="text-xl md:text-display font-serif font-light text-foreground">
                  Wimpern & Augenbrauen
                </h2>
              </div>
              <p className="text-base md:text-lg text-muted-foreground font-light leading-relaxed">
                Natürlicher Schwung für deine Wimpern und Augenbrauen – ganz ohne Extensions. Lash Lifting und Brow Lift sorgen für einen wachen, offenen Blick, der bis zu 8 Wochen hält.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Lash Lift", "Brow Lift", "Lash & Brow Kombi"].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-soft-lg hover-pop">
              <img
                src={lashesImage}
                alt="Lash Lifting & Brow Lift Nürnberg"
                className="w-full h-56 md:h-80 lg:h-[400px] object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8 max-w-5xl mx-auto">
            {/* Lash Lift */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Eye className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Lash Lift</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Wimpernlifting inkl. Färben & Pflege</span>
                    <span className="font-medium">53 €</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs md:text-sm text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>ca. 1 Std.</span>
                  </div>
                </li>
                <li className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Wimpern färben</span>
                    <span className="font-medium">12 €</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs md:text-sm text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>ca. 20 Min.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Brow Lift */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Brow Lift</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Browlifting inkl. Färben & Formen</span>
                    <span className="font-medium">53 €</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs md:text-sm text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>ca. 1 Std.</span>
                  </div>
                </li>
                <li className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Augenbrauen Färben & Formen</span>
                    <span className="font-medium">19 €</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs md:text-sm text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>ca. 30 Min.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Kombi */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Lash & Brow Kombi</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Lash & Browlift komplett</span>
                    <span className="font-medium">79 €</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 text-xs md:text-sm text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>ca. 1,5 Std.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10 md:mt-16">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
              onClick={() => window.open(PLANITY_URL, '_blank')}
            >
              Lash & Brow Termin buchen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default FacialsPage;